/**
 * Position formatting and parsing.
 *
 * Shared, because both frontends display coordinates and both need to accept
 * them, and because a second copy of a coordinate parser is a second set of
 * off-by-one-degree bugs.
 *
 * **Display is degrees + decimal minutes** (`60°23.85'N`), not decimal degrees.
 * That is what a bridge readout shows and what gets read out over VHF;
 * vessel-map made the same call for the same reason. A position a captain has
 * to convert in their head is a position they will transcribe wrongly.
 *
 * Parsing has two entry points because it has two genuinely different jobs:
 *
 * - {@link parseAxis} takes *one* axis from a field a human deliberately typed.
 *   It is lenient about format and strict about meaning: a hemisphere letter
 *   that disagrees with the field it was typed into is an error, not something
 *   to silently reinterpret, because swapped lat/lon is the classic way to end
 *   up 3,000 miles away.
 *
 * - {@link scanForPositions} takes a *blob* of OCR text off a photograph of a
 *   chartplotter and hunts for anything shaped like a coordinate pair. A plotter
 *   screen is covered in numbers -- SOG, COG, depth, XTE, bearing to waypoint,
 *   time -- so this one requires a hemisphere letter before it will believe a
 *   number is a coordinate at all, and returns *candidates with confidence*
 *   rather than an answer.
 *
 * The asymmetry is deliberate. A typed value came from intent; an OCR value
 * came from a guess about a photograph, and is never trusted enough to act on
 * without a human confirming it against the chart.
 */

export type Axis = 'lat' | 'lon'

/**
 * Which way round the digits are, when the typing does not say.
 *
 * `dm` is the default everywhere, because degrees + decimal minutes is what a
 * bridge readout shows and what this app displays (decision 15). The other two
 * exist because ABG's own chart tooling offers exactly these three, so a person
 * copying a position across is choosing between the same names.
 */
export type CoordFormat = 'dm' | 'dd' | 'dms'

/**
 * The three, with both apps' labels and an example each.
 *
 * One registry rather than a list per app, for the same reason the
 * notification kinds are one registry: captain copy is English and admin copy
 * is Norwegian (decision 17), and a format that existed in one and not the
 * other would be a coordinate someone could enter in one app and not the
 * other.
 */
export interface CoordFormatInfo {
  key: CoordFormat
  labelEn: string
  labelNo: string
  /**
   * The field's placeholder, per axis: **digits only, run together.**
   *
   * Digits only because the hemisphere is assumed
   * ({@link withDefaultHemisphere}), so a letter in the example would be an
   * instruction to type something nobody needs to type. Run together because
   * the field is `inputMode="decimal"` and a phone's number pad has no space
   * key -- a spaced example is one the keyboard cannot follow. Spaces still
   * parse, for anyone with a keyboard or a paste.
   */
  exampleLat: string
  exampleLon: string
}

/**
 * Declared as a tuple, not an array, so `COORD_FORMATS[0]` is a value rather
 * than `value | undefined`. Under `noUncheckedIndexedAccess` the array form
 * pushed a `?? fallback` into every call site, which is boilerplate standing
 * in for a fact the type can carry itself: this list is never empty.
 */
export const COORD_FORMATS: readonly [CoordFormatInfo, ...CoordFormatInfo[]] = [
  {
    key: 'dm',
    labelEn: 'Degrees and decimal minutes',
    labelNo: 'Grader og desimalminutter',
    exampleLat: '6023.850',
    exampleLon: '00518.200',
  },
  {
    key: 'dd',
    labelEn: 'Decimal degrees',
    labelNo: 'Desimalgrader',
    exampleLat: '60.397500',
    exampleLon: '5.303333',
  },
  {
    key: 'dms',
    labelEn: 'Degrees, minutes and seconds',
    labelNo: 'Grader, minutter og sekunder',
    exampleLat: '602351',
    exampleLon: '0051812',
  },
]

/** A hemisphere letter. The same four in both apps, untranslated: it is what
 *  {@link formatPosition} prints, what ABG's registry spreadsheet is written
 *  in, and what goes to CSC. */
export type Hemisphere = 'N' | 'S' | 'E' | 'W'

export interface ParsedAxis {
  ok: true
  value: number
  /** How it was understood, for showing back to the captain. */
  interpretation: string
}

export interface ParseFailure {
  ok: false
  error: string
}

export type ParseResult = ParsedAxis | ParseFailure

// --------------------------------------------------------------------- format

/** `60°23.85'N 005°18.20'E` */
export function formatPosition(lat: number, lon: number): string {
  return `${formatLat(lat)} ${formatLon(lon)}`
}

export function formatLat(lat: number): string {
  return degreesMinutes(lat, 'NS', 2)
}

export function formatLon(lon: number): string {
  return degreesMinutes(lon, 'EW', 3)
}

function degreesMinutes(value: number, hemispheres: string, degreeDigits: number): string {
  const hemisphere = value >= 0 ? hemispheres[0] : hemispheres[1]
  const abs = Math.abs(value)
  let degrees = Math.floor(abs)
  let minutes = (abs - degrees) * 60
  // 59.999' rounds to 60.00', which is not a minute that exists. Carry it.
  if (Number(minutes.toFixed(2)) >= 60) {
    minutes = 0
    degrees += 1
  }
  return `${String(degrees).padStart(degreeDigits, '0')}°${minutes
    .toFixed(2)
    .padStart(5, '0')}'${hemisphere}`
}

// ---------------------------------------------------------------------- parse

/** Symbol variants, including what OCR does to them on a backlit LCD. */
const DEGREE_CHARS = "°ºo˚*^~"
const MINUTE_CHARS = "'’‘´`′"
const SECOND_CHARS = '"”“″'

const LIMITS: Record<Axis, { max: number; hemispheres: string; label: string }> = {
  lat: { max: 90, hemispheres: 'NS', label: 'Latitude' },
  lon: { max: 180, hemispheres: 'EW', label: 'Longitude' },
}

/**
 * The hemisphere to assume when the typed text names none.
 *
 * The positive one, read off the same constant {@link parseAxis} refuses
 * against so the two cannot disagree. It is right for every position this
 * system holds: across ABG's 610 anchorages, latitude runs 58.00 to 70.67 and
 * longitude 4.68 to 23.65 -- none south of the equator, none west of
 * Greenwich. "North and west" is a natural way to say Vestlandet and a wrong
 * description of its hemisphere; Bergen is 5°18'E.
 */
export function defaultHemisphere(axis: Axis): Hemisphere {
  return LIMITS[axis].hemispheres[0] as Hemisphere
}

/**
 * A value as digits a number pad can retype: `6756.324`, `01306.139`.
 *
 * The inverse of the run-together reading in {@link parseAxis}, and it exists
 * to prefill the typed-entry field when a captain edits what the camera read.
 * Run-together rather than `67 56.324 N` because that field is
 * `inputMode="decimal"`: a prefill containing a space or a letter is one the
 * captain cannot repair on the device this screen is used on.
 *
 * Minutes are padded to two integer digits, because the run-together split
 * counts them from the right -- `13` and `6.139` must join as `1306.139`, not
 * `136.139`, which is a different meridian.
 *
 * Three decimals: what a plotter prints, and what DD MM.mmm means. Anything
 * less would quietly round the value a captain is trying to correct.
 */
export function toRunTogether(value: number, axis: Axis): string {
  void axis
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  let degrees = Math.floor(abs)
  let minutes = Number(((abs - degrees) * 60).toFixed(3))
  // 59.9996' rounds to 60.000', which is not a minute that exists. Carry it,
  // the same way formatPosition does.
  if (minutes >= 60) {
    minutes = 0
    degrees += 1
  }
  return `${sign}${degrees}${minutes.toFixed(3).padStart(6, '0')}`
}

/**
 * Supply the assumed hemisphere, ready for {@link parseAxis}.
 *
 * This is what lets the coordinate fields be `inputMode="decimal"`. A number
 * pad has no letters, so a captain types digits and the axis's own hemisphere
 * is added for them -- no control to tap, because the answer is the same 610
 * times out of 610.
 *
 * **Added only when the text names none of its own**, which is the whole
 * safety property. A pasted `60 23.850 S` keeps its `S` and parses as south;
 * a signed `-60.3975` keeps its sign; an `E` pasted into the latitude field
 * reaches the parser and earns its "did latitude and longitude get swapped?".
 * Nothing is stripped, so nothing can be silently dropped -- and the
 * hemisphere that was assumed is visible in the read-back before it is
 * committed, on a chart (decision 27).
 *
 * Blank stays blank, so an untouched field still reads as untouched.
 */
export function withDefaultHemisphere(raw: string, axis: Axis): string {
  if (!raw.trim()) return raw
  if (/[nsewNSEW]/.test(raw) || /^\s*-/.test(raw)) return raw
  return `${raw.trim()} ${defaultHemisphere(axis)}`
}

/** The chosen format's labels and examples. Never undefined: an unknown key
 *  falls back to degrees and decimal minutes, which is the default anyway. */
export function coordFormatInfo(key: CoordFormat): CoordFormatInfo {
  return COORD_FORMATS.find((f) => f.key === key) ?? COORD_FORMATS[0]
}

/**
 * Parse one axis from text a human typed.
 *
 * Accepts, in practice everything a captain might reach for:
 *
 *     60.3975            decimal degrees
 *     -60.3975           signed decimal
 *     60 23.85           degrees + decimal minutes
 *     60°23.85'          the same, punctuated
 *     60°23'51"          degrees, minutes, seconds
 *     N 60 23.85         hemisphere first
 *     60 23.85 N         hemisphere last
 *     60,3975            Norwegian decimal comma
 *
 * `format` says how to read digits that are **run together**, the way a
 * plotter or a chart panel prints them:
 *
 *     6023.850           in 'dm'  -> 60° 23.850'
 *     602351             in 'dms' -> 60° 23' 51"
 *
 * It governs **only input this function would otherwise refuse.** Anything
 * that parses today parses identically whatever the format is set to, which is
 * the property that makes the setting safe to add to the riskiest pure code
 * here: none of the fixtures can change meaning, because a run of digits is
 * only reinterpreted once it has already failed the range check for its axis.
 * `60.3975` is a valid latitude, so it stays decimal degrees even in 'dm'.
 *
 * The consequence worth knowing: the setting cannot rescue a genuinely
 * ambiguous mistake. Somebody who means 6° 023.85' and is set to 'dm' gets
 * 60° 23.85'. That is why every caller confirms on a chart before the position
 * is used (decision 27) -- the setting narrows the guessing, the chart catches
 * what is left.
 */
export function parseAxis(raw: string, axis: Axis, format: CoordFormat = 'dm'): ParseResult {
  const { max, hemispheres, label } = LIMITS[axis]
  const text = raw.trim()
  if (!text) return { ok: false, error: `${label} is empty` }

  // Pull out a hemisphere letter wherever it sits, and refuse one that belongs
  // to the other axis. Silently reinterpreting 'E' typed into the latitude
  // field would be the single worst thing this function could do.
  const letters = text.toUpperCase().match(/[NSEW]/g) ?? []
  if (letters.length > 1) {
    return { ok: false, error: `${label} has more than one N/S/E/W` }
  }
  const hemisphere = letters[0]
  if (hemisphere && !hemispheres.includes(hemisphere)) {
    return {
      ok: false,
      error:
        `${label} cannot be ${hemisphere} — did latitude and longitude get ` +
        `swapped? Expected ${hemispheres[0]} or ${hemispheres[1]}.`,
    }
  }

  const negativeSign = /^\s*-/.test(text)
  if (negativeSign && hemisphere) {
    return { ok: false, error: `${label} has both a minus sign and ${hemisphere}` }
  }

  // Everything that is not a number or a decimal separator becomes a space, so
  // the punctuation style stops mattering.
  const cleaned = text
    .toUpperCase()
    .replace(/[NSEW]/g, ' ')
    .replace(new RegExp(`[${escapeClass(DEGREE_CHARS + MINUTE_CHARS + SECOND_CHARS)}]`, 'g'), ' ')
    .replace(/[-–—]/g, ' ')

  const parts = cleaned.match(/\d+(?:[.,]\d+)?/g)
  if (!parts || parts.length === 0) {
    return { ok: false, error: `${label} has no digits in it` }
  }
  if (parts.length > 3) {
    return { ok: false, error: `${label} has too many numbers to read` }
  }

  const numbers = parts.map((p) => Number.parseFloat(p.replace(',', '.')))
  if (numbers.some((n) => Number.isNaN(n))) {
    return { ok: false, error: `${label} could not be read` }
  }

  // One number, too big to be a degree value, and a format that says how the
  // digits are packed: read it as run-together. Nothing already valid reaches
  // this branch, and a split that does not itself validate is discarded rather
  // than reported -- otherwise `181` as a longitude stops saying "must be
  // between 0 and 180" and starts saying "minutes must be under 60", which is
  // a worse answer to a plainer mistake.
  const only = parts.length === 1 ? parts[0] : undefined
  if (only !== undefined && numbers.length === 1 && (numbers[0] ?? 0) > max) {
    const split = splitRunTogether(only.replace(',', '.'), format)
    if (split && runTogetherValue(split) <= max) numbers.splice(0, 1, ...split)
  }

  const [degrees, minutes, seconds] = numbers as [number, number?, number?]
  let value = degrees
  let interpretation: string

  if (minutes === undefined) {
    interpretation = 'decimal degrees'
  } else if (seconds === undefined) {
    if (minutes >= 60) return { ok: false, error: `${label}: minutes must be under 60` }
    value = degrees + minutes / 60
    interpretation = 'degrees and decimal minutes'
  } else {
    if (minutes >= 60) return { ok: false, error: `${label}: minutes must be under 60` }
    if (seconds >= 60) return { ok: false, error: `${label}: seconds must be under 60` }
    if (!Number.isInteger(minutes)) {
      return { ok: false, error: `${label}: give minutes as a whole number when using seconds` }
    }
    value = degrees + minutes / 60 + seconds / 3600
    interpretation = 'degrees, minutes and seconds'
  }

  if (value > max) {
    return { ok: false, error: `${label} must be between 0 and ${max}` }
  }

  const southOrWest = hemisphere === 'S' || hemisphere === 'W'
  if (southOrWest || negativeSign) value = -value

  return { ok: true, value, interpretation }
}

function escapeClass(chars: string): string {
  return chars.replace(/[\\\]^-]/g, '\\$&')
}

/**
 * `6023.850` -> [60, 23.850].  `602351` -> [60, 23, 51].
 *
 * The minutes are always the **last two** integer digits, and the seconds the
 * two before any decimal -- which is what makes this readable at all: degrees
 * are one to three digits depending on the axis and whether the leading zero
 * was typed, but minutes and seconds are always two. Counting from the right
 * is therefore the only way that works for `623.85` and `06023.85` alike.
 *
 * Returns null when there are too few digits to split. It does *not* judge
 * whether the split is sensible -- {@link runTogetherValue} does that, and the
 * caller discards a split that fails. Keeping the two apart is what lets `181`
 * as a longitude still report "must be between 0 and 180": the split to
 * `1° 81'` is produced, found invalid, and thrown away, so the plainer error
 * survives.
 */
/**
 * The value a split would produce, or Infinity if it is not a coordinate at
 * all. Infinity rather than null so the caller's single comparison covers both
 * "out of range" and "not a valid split" -- there is nothing to tell apart,
 * because both mean *do not use this reading*.
 */
function runTogetherValue(split: number[]): number {
  const [degrees, minutes, seconds] = split as [number, number, number?]
  if (minutes >= 60) return Number.POSITIVE_INFINITY
  if (seconds === undefined) return degrees + minutes / 60
  if (seconds >= 60 || !Number.isInteger(minutes)) return Number.POSITIVE_INFINITY
  return degrees + minutes / 60 + seconds / 3600
}

function splitRunTogether(token: string, format: CoordFormat): number[] | null {
  if (format === 'dd') return null
  // Sliced rather than destructured from `split('.')`: under
  // `noUncheckedIndexedAccess` both halves are `string | undefined`, and the
  // compiler is right to insist -- a token is not guaranteed to contain a dot.
  const dot = token.indexOf('.')
  const whole = dot === -1 ? token : token.slice(0, dot)
  const fraction = dot === -1 ? '' : token.slice(dot + 1)
  const need = format === 'dms' ? 5 : 3
  if (whole.length < need) return null

  const chunk = format === 'dms' ? 4 : 2
  const degrees = Number.parseInt(whole.slice(0, whole.length - chunk), 10)
  if (format === 'dm') {
    const minutes = Number.parseFloat(
      whole.slice(-2) + (fraction ? `.${fraction}` : ''),
    )
    return [degrees, minutes]
  }
  const minutes = Number.parseInt(whole.slice(-4, -2), 10)
  const seconds = Number.parseFloat(whole.slice(-2) + (fraction ? `.${fraction}` : ''))
  return [degrees, minutes, seconds]
}

// ------------------------------------------------------------------ ocr scan

export interface PositionCandidate {
  lat: number
  lon: number
  /** 0..1. Below ~0.5 the UI should be visibly unsure about it. */
  confidence: number
  /** The exact substring this came from, shown so the captain can compare. */
  matched: string
}

/**
 * Degrees, then optionally decimal minutes, then optionally seconds.
 *
 * Inner whitespace is `[ \t]`, never `\s` — a plotter panel puts one axis per
 * line, so a token must never be allowed to span a newline and glue the
 * latitude's degrees to the longitude's minutes.
 *
 * **Every number is followed by `(?!\\d)`, and that closes a wrong-answer
 * hole.** Without it a group may consume part of a digit run and leave the
 * rest, so `67°56 324' N` matched the substring `324' N` as 3° 24' -- a valid,
 * in-range, **confidently wrong** latitude of 3.4°N, off a screen reading
 * 67°56.324'N. Found 2026-09-08 while adding the lost-separator repair, and it
 * is a third instance of the class this docstring already lists.
 *
 * Seconds are `\\d{1,3}`, not `\\d{1,2}`, for the opposite reason. Three
 * digits cannot be seconds, so `56 324` is matched **whole**, refused by
 * {@link parseAxis}, and {@link rejoinLostDecimal} gets its chance to supply
 * the separator OCR dropped. Widening the pattern to let an invalid reading
 * *through* is what makes a narrow, checkable repair possible rather than a
 * guess at a shorter valid substring.
 *
 * And the third number may be closed by a **minute** mark as well as a second
 * one. `67°56.324'` with its separator lost reads `67°56 324'`, where the
 * apostrophe still belongs to the minutes but now follows the fraction. Which
 * mark closes the third number says nothing reliable about what that number
 * is; `parseAxis` decides that from its value. It also covers a genuine
 * `60°23'51"` whose closing double prime came back as a single.
 */
const MAGNITUDE =
  `\\d{1,3}(?!\\d)[ \\t]{0,2}[${escapeClass(DEGREE_CHARS)}]?[ \\t]{0,2}` +
  `(?:\\d{1,2}(?:[.,]\\d{1,4})?(?!\\d)[ \\t]{0,2}[${escapeClass(MINUTE_CHARS)}]?` +
  `(?:[ \\t]{0,2}\\d{1,3}(?:[.,]\\d{1,3})?(?!\\d)[ \\t]{0,2}` +
  `[${escapeClass(SECOND_CHARS + MINUTE_CHARS)}]?)?)?`

/**
 * One axis of a coordinate, as it appears on a plotter screen.
 *
 * A hemisphere letter is **required**, and that is the whole trick: it is what
 * separates `67°51.220'N` from the speed, the course, the depth, the distance
 * to waypoint and the fifteen other numbers on the same screen. Three failures
 * from a real photograph of a Furuno panel, all fixed here:
 *
 * - the `N` in the *label* `LON` was read as a hemisphere, poisoning the
 *   longitude token and losing the whole pair;
 * - `34.70NM` (distance to waypoint) parsed as latitude 34°N;
 * - `22 WGS8` (a depth sounding next to the datum label) parsed as 22°W.
 *
 * The last two are the dangerous ones — plausible-looking wrong answers. The
 * lookbehind and lookahead below are what refuse a hemisphere letter that is
 * glued to another letter.
 *
 * The two branches are deliberate rather than one pattern with both an optional
 * leading and trailing hemisphere. That version reached across the whitespace
 * between two axes and claimed the *next* axis's leading `E` as the latitude's
 * trailing hemisphere, so `N 60°23.850'  E 005°18.200'` produced nothing at
 * all. Branch order matters: leading form first, so it wins that race.
 *
 * Lookbehind needs Safari 16.4+ — the same floor Web Push on an installed iOS
 * PWA already imposes, so no new constraint.
 */
const AXIS_PATTERN = new RegExp(
  // Hemisphere first: `N 67°51.220'`
  `(?<![A-Za-z])(?<lead>[NSEW])[ \\t]{0,3}${MAGNITUDE}` +
    '|' +
    // Hemisphere last: `67°51.220' N`
    `${MAGNITUDE}[ \\t]{0,3}(?<trail>[NSEW])(?![A-Za-z])`,
  'gi',
)

interface AxisHit {
  axis: Axis
  value: number
  index: number
  length: number
  matched: string
  hadDegreeSymbol: boolean
  hadMinuteSymbol: boolean
  /** True when a decimal separator had to be supplied. Costs confidence. */
  repaired?: boolean
}

/**
 * Hunt a blob of OCR text for coordinate pairs.
 *
 * Returns every plausible pair, best first. Returning several is the point: if
 * the plotter shows both the cursor position and the vessel position, the
 * captain is the only one who knows which they meant.
 */
export function scanForPositions(text: string): PositionCandidate[] {
  const hits: AxisHit[] = []
  // A pipe is the commonest OCR substitution for the digit 1 in the
  // condensed fonts plotters use; non-breaking spaces arrive via copy-paste.
  const normalised = text.replace(/\|/g, '1').replace(/ /g, ' ')

  AXIS_PATTERN.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = AXIS_PATTERN.exec(normalised)) !== null) {
    if (match[0].trim() === '') {
      // Zero-width match; step forward or we spin here forever.
      AXIS_PATTERN.lastIndex += 1
      continue
    }
    const groups = match.groups ?? {}
    // Exactly one of the two branches matched, so exactly one of these is set.
    const hemisphere = (groups.lead ?? groups.trail ?? '').toUpperCase()
    if (!hemisphere) continue

    const axis: Axis = hemisphere === 'N' || hemisphere === 'S' ? 'lat' : 'lon'
    const direct = parseAxis(match[0], axis)
    // Only where the parser has already refused, and only for a separator OCR
    // dropped. See rejoinLostDecimal for what is deliberately not recovered.
    const recovered = direct.ok ? null : rejoinLostDecimal(match[0], axis)
    const parsed = direct.ok ? direct : recovered
    if (!parsed) continue

    hits.push({
      axis,
      value: parsed.value,
      index: match.index,
      length: match[0].length,
      matched: match[0].trim(),
      hadDegreeSymbol: new RegExp(`[${escapeClass(DEGREE_CHARS)}]`).test(match[0]),
      hadMinuteSymbol: new RegExp(`[${escapeClass(MINUTE_CHARS)}]`).test(match[0]),
      repaired: recovered !== null,
    })
  }

  // Pair each latitude with the nearest longitude, in either direction --
  // plotters disagree about the order and OCR may put them on separate lines.
  const latitudes = hits.filter((h) => h.axis === 'lat')
  const longitudes = hits.filter((h) => h.axis === 'lon')
  const used = new Set<AxisHit>()
  const candidates: PositionCandidate[] = []

  for (const lat of latitudes) {
    let best: AxisHit | null = null
    let bestDistance = Number.POSITIVE_INFINITY
    for (const lon of longitudes) {
      if (used.has(lon)) continue
      const distance = Math.abs(lon.index - (lat.index + lat.length))
      if (distance < bestDistance) {
        best = lon
        bestDistance = distance
      }
    }
    if (!best) continue
    used.add(best)
    candidates.push({
      lat: lat.value,
      lon: best.value,
      matched: `${lat.matched} ${best.matched}`,
      confidence: score(lat, best, bestDistance),
    })
  }

  return candidates.sort((a, b) => b.confidence - a.confidence)
}

/**
 * How much to believe a candidate.
 *
 * Punctuation surviving OCR is the strongest signal that the read was clean; a
 * pair sitting next to each other in the text is the next strongest. Being
 * inside ABG's coverage area is worth something too, but only a little -- it
 * must not be so heavy that a genuine position outside Norway scores as noise.
 */
/**
 * Rejoin minutes whose decimal separator OCR read as a space.
 *
 * `67°56 324' N` is three numbers, and as degrees-minutes-seconds it is
 * refused: 324 seconds does not exist. In degrees and decimal minutes it can
 * only be `56.324`, so the separator is supplied and nothing else changes --
 * the digits are untouched and in order.
 *
 * **Deliberately narrow.** The same read also produced `67°561324'`, where the
 * separator came through as the digit `1`. That is *not* recovered, and must
 * not be: turning it into `56.324` needs a digit deleted, and nothing in the
 * string says which. Inserting a decimal after two digits gives 56.1324 --
 * a different position, about 340 m away, offered with confidence. A refusal
 * is worth more than that.
 *
 * Returns null unless the third number cannot be seconds *and* the rejoined
 * value validates, so it can only ever turn a refusal into a reading.
 */
function rejoinLostDecimal(matched: string, axis: Axis): ParsedAxis | null {
  const parts = matched.match(/\d+(?:[.,]\d+)?/g)
  if (!parts || parts.length !== 3) return null
  const [degrees, minutes, fraction] = parts as [string, string, string]
  // Any of these would make it a plausible D M S reading, and then guessing
  // would be guessing rather than supplying a separator.
  if (fraction.length < 3) return null
  if (/[.,]/.test(minutes) || /[.,]/.test(fraction)) return null
  if (Number(fraction) < 60) return null

  // Hand it back to parseAxis with the hemisphere intact, so the sign and
  // range rules stay in one place rather than being second-guessed here.
  const rejoined = `${degrees} ${minutes}.${fraction}`
  const hemisphere = matched.toUpperCase().match(/[NSEW]/)?.[0] ?? ''
  const attempt = parseAxis(`${rejoined} ${hemisphere}`.trim(), axis)
  return attempt.ok ? attempt : null
}

function score(lat: AxisHit, lon: AxisHit, gap: number): number {
  let confidence = 0.4 // it had hemisphere letters at all
  if (lat.hadDegreeSymbol && lon.hadDegreeSymbol) confidence += 0.15
  if (lat.hadMinuteSymbol && lon.hadMinuteSymbol) confidence += 0.15
  if (gap <= 40) confidence += 0.15
  if (isNorwegianWaters(lat.value, lon.value)) confidence += 0.15
  // A supplied separator is the one thing here that changes the *value*
  // rather than merely reading it, so it caps confidence rather than costing
  // it a few points: a repaired reading is never a confident one, however
  // many of the signals above it collects. 0.45 sits below the 0.5 that
  // PositionCandidate tells the UI to be visibly unsure under.
  if (lat.repaired || lon.repaired) confidence = Math.min(confidence, 0.45)
  return Math.max(0, Math.min(1, Number(confidence.toFixed(2))))
}

/**
 * ABG's coverage area, generously drawn. Used only to rank and to warn -- never
 * to reject. Where the agency works is theirs to define, not this module's.
 */
export function isNorwegianWaters(lat: number, lon: number): boolean {
  return lat >= 56 && lat <= 81 && lon >= -2 && lon <= 36
}

/** Metres between two WGS84 points. Haversine. */
export function distanceM(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const R = 6_371_000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLon / 2) ** 2 * Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat))
  return 2 * R * Math.asin(Math.sqrt(h))
}
